<template>
  <b-container class="main-container">
    <b-modal id="approve-modal" title="Update Status" @ok="submitBuyout">
      <p>Press 'OK' to mark this request with the selected status.</p>
      <p>You may also leave a note with this status change, which will be visible to the faculty member who made this buyout request.</p>
      
      <b-form-group label="Status" label-class="control-label workload-course-label" :class="{ 'invalid-group': $v.form.statusId.$error }">
        <b-form-select :options="statuses" v-model="form.statusId" text-field="name" value-field="id" :class="{ 'form-control': true, 'is-invalid': $v.form.statusId.$error }"></b-form-select>
      </b-form-group>
      
      <b-form-group label="Notes" label-class="control-label workload-course-label">
        <b-form-textarea :rows="3" placeholder="(Optional) Enter notes..." v-model="form.statusNotes"></b-form-textarea>
      </b-form-group>

    </b-modal>

    <b-modal id="grants-modal" title="Manage Grants" v-model="openGrantModal" @ok.prevent="attemptGrantSubmit = true">
      <buyout-grant-approvals v-if="buyoutRequest"
                  v-model="attemptGrantSubmit"
                  :grants="buyoutRequest.grants"
                  :total-percent="buyoutRequest.buyoutPercent"
                  :buyout-id="buyoutRequest.id"
                   v-on:complete="saveGrants"></buyout-grant-approvals>
    </b-modal>

    <b-modal id="edit-modal" title="Edit Buyout" v-model="openBuyoutModal" @ok.prevent="attemptBuyoutSubmit = true" >
      <buyout-form v-model="attemptBuyoutSubmit"
                   :terms="buyoutRequest.workload.timePeriod.timePeriodTerms"
                   :editingBuyout="buyoutRequest"
                   v-if="buyoutRequest"
                   v-on:complete="saveBuyout"></buyout-form>
    </b-modal>

    <h3 v-if="buyoutRequest">Buyout Request
      <!-- <b-button style="float:right" class="ml-2" variant="primary">Change Status</b-button> -->
      <b-button v-if="isBuyoutManager" v-b-modal.edit-modal variant="primary" class="ml-2 pull-right">Edit</b-button>
      <b-button v-if="isBuyoutManager" v-b-modal.approve-modal variant="primary" class="ml-2 pull-right">Change Status</b-button>
    </h3>

    <h4 v-if="buyoutRequest">{{ buyoutRequest.workload.timePeriod.description }} - {{ buyoutRequest.employee.firstName }} {{ buyoutRequest.employee.lastName }} {{ buyoutRequest.term ? " - " + buyoutRequest.term.description : "" }}</h4>

    <b-row v-if="buyoutRequest">
      <b-col>
        <widget heading="Details">
          <b-row>
            <b-col>
              <dl>
                <dt>Current Status</dt>
                <dd>{{ buyoutRequest.currentStatus.statusName }}</dd>
                <dt>Number of Classes</dt>
                <dd>{{ buyoutRequest.numberOfClasses}}</dd>
                <dt>Has this buyout been discussed with the appropriate program director?</dt>
                <dd>{{ buyoutRequest.programDirector ? "Yes" : "No "}}</dd>
                <dt>Program Director</dt>
                <dd>{{ buyoutRequest.programDirector }}</dd>
              </dl>
            </b-col>
            <b-col>
              <dl>
                <dt>Notes</dt>
                <dd>{{ buyoutRequest.notes }}</dd>
              </dl>
            </b-col>
          </b-row>
        </widget>
        <widget heading="Grant Preferences">
          <buyout-grants :grants="requestedGrants" :manage="false" />
        </widget>
        <widget heading="Approved Grants">
          <buyout-grants :grants="approvedGrants" :manage="isBuyoutManager">
            <template slot="grant-buttons">
              <b-button
                v-if="isBuyoutManager"
                class="mx-2" variant="primary" v-b-modal.grants-modal @click=""><font-awesome-icon icon="plus"/> Manage Approved Grants</b-button>
            </template>
          </buyout-grants>
        </widget>
        <widget heading="History">
          <buyout-history :statuses="buyoutRequest.statuses" />
        </widget>        
        <widget heading="Supporting Documents">
          <workload-files :editable="isBuyoutManager" :files="mappedFiles" @fileAdded="addFile" @fileRemoved="removeFile"></workload-files>
        </widget>
      </b-col>
    </b-row>
    <b-button v-if="isBuyoutManager" v-b-modal.edit-modal variant="primary" class="ml-2 pull-right">Edit</b-button>
    <b-button v-if="isBuyoutManager" v-b-modal.approve-modal variant="primary" class="ml-2 pull-right">Change Status</b-button>
  </b-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import buyoutRequestApi from "../../api/buyouts";
import Widget from "../../components/widget";
import BuyoutGrants from "../../components/workloads/buyout-grants";
import BuyoutHistory from "../../components/workloads/buyout-history";
import BuyoutForm from "../../components/workloads/buyout-form";
import BuyoutGrantApprovals from "../../components/workloads/buyout-grant-approvals";
import WorkloadFiles from "../../components/workloads/workload-files";
import ToastrSettings from "../../settings/toastr-settings";
import toastr from "toastr";
import roleChecks from "../../misc/role-checks";
import { required } from "vuelidate/lib/validators";

export default {
  mixins: [roleChecks],
  name: "BuyoutDetails",
  data() {
    return {
      openBuyoutModal: false,
      openGrantModal: false,
      attemptBuyoutSubmit: false,
      attemptGrantSubmit: false,
      buyoutRequest: null,
      form: {
        statusId: null,
        statusNotes: null,
      },
      statuses: [],
    };
  },

  props: {
    id: {
      required: true,
    },
  },

  components: {
    widget: Widget,
    BuyoutGrants,
    BuyoutHistory,
    WorkloadFiles,
    BuyoutForm,
    BuyoutGrantApprovals,
  },

  validations: {
    form: {
      statusId: { required },
      notes: {},
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      files: (state) => state.workloads.files,
    }),
    requestedGrants() {
      return _.filter(this.buyoutRequest.grants, (grant) => {
        return grant.requested;
      });
    },
    approvedGrants() {
      return _.filter(this.buyoutRequest.grants, (grant) => {
        return grant.approved;
      });
    },
    mappedFiles() {
      return _.map(this.buyoutRequest.workload.workloadFiles, function(
        workloadFile
      ) {
        return {
          fileId: workloadFile.file.fileId,
          name: workloadFile.file.fileName,
          createdBy: workloadFile.file.createdBy,
          createdDate: workloadFile.file.createdDate,
          _rowVariant: workloadFile.file._rowVariant
            ? workloadFile.file._rowVariant
            : null,
        };
      });
    },
  },

  methods: {
    ...mapActions("workloads", ["addWorkloadFiles"]),
    load: function() {
      buyoutRequestApi
        .getById(this.id)
        .then((response) => {
          this.buyoutRequest = response.data;
          this.form.statusId = this.buyoutRequest.statusId;
        })
        .catch(function(error) {
          console.log(error);
        });
    },
    addApprovedGrant(grant) {
      this.buyoutRequest.grants.push(grant);
    },
    removeGrant(grant) {
      var index = _.findIndex(this.form.grants, (item) => {
        return item.description === grant.description;
      });
      this.form.grants.splice(index, 1);
    },
    saveBuyout(buyout) {
      if (buyout) {
        this.attemptBuyoutSubmit = false;
        buyoutRequestApi.putBuyout(buyout).then(() => {
          toastr.options = ToastrSettings;
          toastr.success("The request has been saved.", "Success");
          this.load();
        });
        this.openBuyoutModal = false;
      }
    },
    saveGrants(grants) {
      if (grants) {
        this.attemptGrantSubmit = false;
        this.buyoutRequest.grants = grants;
        buyoutRequestApi.putBuyout(this.buyoutRequest).then(() => {
          toastr.options = ToastrSettings;
          toastr.success("The request has been saved.", "Success");
          this.load();
        });
        this.openGrantModal = false;
      }
    },
    submitBuyout() {
      let updatedBuyout = {
        buyoutRequestId: this.buyoutRequest.id,
        buyoutRequestStatusDefinitionId: this.form.statusId,
        notes: this.form.statusNotes,
      };

      buyoutRequestApi.postStatus(updatedBuyout).then(() => {
        buyoutRequestApi.statusEmail(this.buyoutRequest.id);
        toastr.options = ToastrSettings;
        toastr.success("The request has been saved.", "Success");
        this.load();
      });
    },

    addFile(files) {
      var that = this;
      this.$emit("fileAdded", files);
      _.forEach(files, function(file) {
        that.files.push(file);
        that.buyoutRequest.workload.workloadFiles.unshift({
          workloadId: that.buyoutRequest.workload.workloadId,
          file: {
            fileName: file.name,
            createdBy: that.user.userId,
            createdDate: new Date(),
            _rowVariant: "success",
          },
        });
      });

      this.addWorkloadFiles(this.buyoutRequest.workload.workloadId).then(() => {
        this.load();
      });
    },

    removeFile(file) {
      this.$emit("fileRemoved", file);
    },
  },

  created() {
    this.load();
    buyoutRequestApi.getStatusDefinitions().then((response) => {
      this.statuses = response.data;
    });
  },
};
</script>

<style scoped>
.main-container {
  max-width: 100%;
  margin-top: 10px;
}
</style>
